import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import { materialId, purchaseUnitOf, supplierId } from './wms-master.seed'
import { type PrcStatusCode, prcActionId, prcActorId, prcStatusId, WF_PRC_01_ID } from './workflow-prc.seed'

/**
 * Transaksi Procurement: purchase_request + purchase_request_line beserta jejak Workflow Studio
 * (wxl_submission, wxl_submission_log) yang konsisten dengan WF-PRC-01.
 * PR 001–007 = seed prototype `proc.pr`. PR 008–009 [A] ditambahkan agar antrean Purchasing (L2)
 * dan status Rejected punya contoh data.
 */

type Step = [
  action: string,
  actor: 'ACT_PRC_ADMIN_RM' | 'ACT_PRC_SPV_RM' | 'ACT_PRC_PURCHASING',
  at: string,
  reason?: string
]

type PrSeed = {
  no: string
  lines: [material: string, qty: number, supplier: string, remark?: string][]
  createdAt: string
  steps: Step[]
}

const ADMIN = USER_IDS.yulia
const ACTOR_USER: Record<Step[1], string> = {
  ACT_PRC_ADMIN_RM: USER_IDS.yulia,
  ACT_PRC_SPV_RM: USER_IDS.galih,
  ACT_PRC_PURCHASING: USER_IDS.rudi
}

const FULL_APPROVAL = (submit: string, spv: string, purch: string): Step[] => [
  ['ACT_PRC_SUBMIT', 'ACT_PRC_ADMIN_RM', submit],
  ['ACT_PRC_APP_SPV', 'ACT_PRC_SPV_RM', spv],
  ['ACT_PRC_APP_PURCH', 'ACT_PRC_PURCHASING', purch]
]

const PURCHASE_REQUESTS: PrSeed[] = [
  {
    no: '001/PR/08/2026',
    lines: [['FOIL-BLK86', 10, 'SUP-BMJ']],
    createdAt: '2026-08-10T08:20:00+07:00',
    steps: [['ACT_PRC_SUBMIT', 'ACT_PRC_ADMIN_RM', '2026-08-10T08:22:00+07:00']]
  },
  {
    no: '002/PR/08/2026',
    lines: [['ETK-ONE12', 8, 'SUP-KH']],
    createdAt: '2026-08-05T07:10:00+07:00',
    steps: FULL_APPROVAL('2026-08-05T09:00:00+07:00', '2026-08-05T13:40:00+07:00', '2026-08-06T09:15:00+07:00')
  },
  {
    no: '003/PR/08/2026',
    lines: [['PPR-REG', 20, 'SUP-BMJ']],
    createdAt: '2026-08-03T07:00:00+07:00',
    steps: FULL_APPROVAL('2026-08-03T10:15:00+07:00', '2026-08-03T15:00:00+07:00', '2026-08-04T08:30:00+07:00')
  },
  {
    no: '004/PR/08/2026',
    lines: [['INN-BLK96', 10, 'SUP-KH']],
    createdAt: '2026-08-01T07:00:00+07:00',
    steps: FULL_APPROVAL('2026-08-01T09:30:00+07:00', '2026-08-01T14:00:00+07:00', '2026-08-02T10:00:00+07:00')
  },
  {
    no: '005/PR/07/2026',
    lines: [['FLT-B06', 10, 'SUP-FLI']],
    createdAt: '2026-07-28T07:00:00+07:00',
    steps: FULL_APPROVAL('2026-07-28T09:00:00+07:00', '2026-07-28T13:00:00+07:00', '2026-07-29T09:00:00+07:00')
  },
  {
    no: '006/PR/08/2026',
    lines: [['LEM-C', 5, 'SUP-KTM']],
    createdAt: '2026-08-12T07:00:00+07:00',
    steps: FULL_APPROVAL('2026-08-12T08:00:00+07:00', '2026-08-12T10:30:00+07:00', '2026-08-13T08:30:00+07:00')
  },
  {
    no: '007/PR/08/2026',
    lines: [['KRF-75', 200, 'SUP-IJ']],
    createdAt: '2026-08-09T07:00:00+07:00',
    steps: FULL_APPROVAL('2026-08-09T08:00:00+07:00', '2026-08-10T09:00:00+07:00', '2026-08-11T08:00:00+07:00')
  },
  {
    no: '008/PR/08/2026',
    lines: [
      ['FOIL-GLD70', 6, 'SUP-BMJ', 'Untuk SPK Bold minggu depan'],
      ['TT-GLDH', 4, 'SUP-IJ']
    ],
    createdAt: '2026-08-14T08:00:00+07:00',
    steps: [
      ['ACT_PRC_SUBMIT', 'ACT_PRC_ADMIN_RM', '2026-08-14T08:05:00+07:00'],
      ['ACT_PRC_APP_SPV', 'ACT_PRC_SPV_RM', '2026-08-14T13:20:00+07:00']
    ]
  },
  {
    no: '009/PR/08/2026',
    lines: [['OPP-125', 30, 'SUP-BMJ']],
    createdAt: '2026-08-15T09:00:00+07:00',
    steps: [
      ['ACT_PRC_SUBMIT', 'ACT_PRC_ADMIN_RM', '2026-08-15T09:05:00+07:00'],
      [
        'ACT_PRC_REJ_SPV',
        'ACT_PRC_SPV_RM',
        '2026-08-15T11:40:00+07:00',
        'Qty terlalu besar — stok OPP-125 masih cukup 2 minggu, turunkan ke 15 Roll.'
      ]
    ]
  }
]

const ACTION_TO: Record<string, PrcStatusCode> = {
  ACT_PRC_SUBMIT: 'PRC_PENDING_SPV',
  ACT_PRC_APP_SPV: 'PRC_PENDING_PURCH',
  ACT_PRC_REJ_SPV: 'PRC_REJECTED_SPV',
  ACT_PRC_APP_PURCH: 'PRC_APPROVED',
  ACT_PRC_REJ_PURCH: 'PRC_REJECTED_PURCH'
}

const ACTION_FROM: Record<string, PrcStatusCode> = {
  ACT_PRC_SUBMIT: 'PRC_DRAFT',
  ACT_PRC_APP_SPV: 'PRC_PENDING_SPV',
  ACT_PRC_REJ_SPV: 'PRC_PENDING_SPV',
  ACT_PRC_APP_PURCH: 'PRC_PENDING_PURCH',
  ACT_PRC_REJ_PURCH: 'PRC_PENDING_PURCH'
}

/** Directus mengembalikan timestamptz sebagai ISO UTC (`...Z`); seed mengikuti bentuk itu. */
const utc = (at: string) => new Date(at).toISOString()

const audit = (userId: string, at: string) => ({
  status: 'published',
  sort: null,
  user_created: userId,
  date_created: utc(at),
  user_updated: userId,
  date_updated: utc(at)
})

export function buildWmsProcurementSeed(): SeedTables {
  const requests: LocalRow[] = []
  const lines: LocalRow[] = []
  const submissions: LocalRow[] = []
  const logs: LocalRow[] = []

  PURCHASE_REQUESTS.forEach((pr, prIndex) => {
    const prId = seedId(SEED_GROUP.purchaseRequest, prIndex + 1)
    const submissionId = seedId(SEED_GROUP.wxlSubmission, 100 + prIndex + 1)
    const lastStep = pr.steps[pr.steps.length - 1]
    const finalStatus = ACTION_TO[lastStep[0]]
    const rejection = pr.steps.find(step => step[3])

    requests.push({
      ...audit(ADMIN, pr.createdAt),
      user_updated: ACTOR_USER[lastStep[1]],
      date_updated: utc(lastStep[2]),
      id: prId,
      pr_no: pr.no,
      status_id: prcStatusId(finalStatus),
      submission_id: submissionId,
      stage: finalStatus,
      rejection_reason: rejection?.[3] ?? null,
      ...(finalStatus === 'PRC_APPROVED' ? { is_approved: true, approved_date: utc(lastStep[2]) } : {})
    })

    pr.lines.forEach(([material, qty, supplier, remark], lineIndex) => {
      lines.push({
        ...audit(ADMIN, pr.createdAt),
        id: seedId(SEED_GROUP.purchaseRequestLine, (prIndex + 1) * 10 + lineIndex),
        pr_id: prId,
        material_id: materialId(material),
        qty,
        unit_id: purchaseUnitOf(material),
        supplier_id: supplierId(supplier as Parameters<typeof supplierId>[0]),
        remark: remark ?? null,
        source: 'MANUAL'
      })
    })

    submissions.push({
      ...audit(ADMIN, pr.steps[0][2]),
      id: submissionId,
      workflow_id: WF_PRC_01_ID,
      entity: 'purchase_request',
      entity_id: prId,
      source: 'ACTION',
      current_status_id: prcStatusId(finalStatus),
      last_action_id: prcActionId(lastStep[0]),
      workflow_snapshot: null
    })

    pr.steps.forEach(([action, actor, at, reason], stepIndex) => {
      logs.push({
        ...audit(ACTOR_USER[actor], at),
        id: seedId(SEED_GROUP.wxlSubmissionLog, (prIndex + 1) * 10 + stepIndex),
        workflow_id: WF_PRC_01_ID,
        submission_id: submissionId,
        action_id: prcActionId(action),
        actor_id: prcActorId(actor),
        from_status_id: prcStatusId(ACTION_FROM[action]),
        to_status_id: prcStatusId(ACTION_TO[action]),
        approval_total_count: 1,
        approval_approved_count: action.includes('REJ') ? 0 : 1,
        approval_rejected_count: action.includes('REJ') ? 1 : 0,
        reason: reason ?? null,
        entity: 'purchase_request',
        entity_id: prId
      })
    })
  })

  return {
    purchase_request: requests,
    purchase_request_line: lines,
    wxl_submission: submissions,
    wxl_submission_log: logs
  }
}
